import type {
    SiteBuilderBlock,
    SiteBuilderRuntimePageLink,
    SiteBuilderRuntimeSite,
} from "./siteBuilderTypes";
import {
    siteBuilderFieldValue,
} from "./siteBuilderFieldValues";

export { siteBuilderCountdownValues } from "./siteBuilderCountdownModel";

export type SiteBuilderMediaPresentation =
    | { kind: "empty"; source: "" }
    | { kind: "image"; source: string }
    | { kind: "video"; source: string };

export type SiteBuilderSearchSuggestion = {
    href: string;
    label: string;
};

function nonEmptyText(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
}

function finiteNumber(value: unknown, fallback: number): number {
    const numeric = typeof value === "number" ? value : Number(value);

    return Number.isFinite(numeric) ? numeric : fallback;
}

export function siteBuilderProgressValue(block: SiteBuilderBlock): number {
    return Math.max(
        0,
        Math.min(
            100,
            finiteNumber(
                siteBuilderFieldValue(block, "content", "progressValue"),
                0,
            ),
        ),
    );
}

export function siteBuilderRatingPresentation(
    block: SiteBuilderBlock,
): { max: number; value: number } {
    const max = Math.max(
        1,
        Math.min(
            20,
            Math.round(finiteNumber(block.content?.ratingMax, 5)),
        ),
    );

    return {
        max,
        value: Math.max(
            0,
            Math.min(
                max,
                finiteNumber(
                    siteBuilderFieldValue(
                        block,
                        "content",
                        "ratingValue",
                    ),
                    5,
                ),
            ),
        ),
    };
}

export function siteBuilderAspectRatio(value: string): string | undefined {
    if (value === "wide") return "16 / 9";
    if (value === "standard") return "4 / 3";
    if (value === "square") return "1 / 1";
    if (value === "portrait") return "3 / 4";

    return /^\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?$/u.test(value)
        ? value
        : undefined;
}

export function siteBuilderAvatarInitials(value: string): string {
    const words = value
        .trim()
        .split(/\s+/u)
        .filter(Boolean);

    if (words.length === 0) {
        return "?";
    }

    const first = words[0]?.slice(0, 1) ?? "";
    const last = words.length > 1
        ? words.at(-1)?.slice(0, 1) ?? ""
        : words[0]?.slice(1, 2) ?? "";

    return `${first}${last}`.toLocaleUpperCase();
}

export function siteBuilderMediaPresentation(
    content: Record<string, unknown> | null | undefined,
): SiteBuilderMediaPresentation {
    const source = content ?? {};
    const video = [
        source.videoUrl,
        source.embedUrl,
    ].map(nonEmptyText).find(Boolean);

    if (video) {
        return { kind: "video", source: video };
    }

    const image = [
        source.image,
        source.imageUrl,
        source.src,
        source.url,
    ].map(nonEmptyText).find(Boolean);

    return image
        ? { kind: "image", source: image }
        : { kind: "empty", source: "" };
}

export function siteBuilderSearchSuggestions(
    pages: SiteBuilderRuntimePageLink[],
    site: SiteBuilderRuntimeSite | null,
    searchWithin: string,
    rawLimit: unknown,
): SiteBuilderSearchSuggestion[] {
    const numericLimit = typeof rawLimit === "number"
        ? rawLimit
        : Number(rawLimit);
    const limit = Number.isFinite(numericLimit)
        ? Math.max(1, Math.min(20, Math.trunc(numericLimit)))
        : 6;
    const suggestions = pages.map((page) => ({
        href: page.href ?? `/${page.slug}`,
        label: page.title,
    }));

    if (searchWithin === "everything" && site?.name.trim()) {
        suggestions.unshift({
            href: pages.find((page) => page.isHomepage)?.href ?? "/",
            label: site.name.trim(),
        });
    }

    const seen = new Set<string>();

    return suggestions.filter((item) => {
        const key = item.label.toLocaleLowerCase();

        if (seen.has(key)) {
            return false;
        }

        seen.add(key);

        return true;
    }).slice(0, limit);
}

function normalizedSearchText(value: string): string {
    return value
        .normalize("NFKD")
        .replace(/\p{Diacritic}/gu, "")
        .trim()
        .toLocaleLowerCase();
}

/**
 * Resolves a page-search query deterministically. Exact labels win, followed
 * by label prefixes and then partial label/URL matches.
 */
export function siteBuilderSearchTarget(
    suggestions: readonly SiteBuilderSearchSuggestion[],
    query: string,
): string | null {
    const normalizedQuery = normalizedSearchText(query);

    if (normalizedQuery === "") {
        return null;
    }

    const candidates = suggestions.map((suggestion) => ({
        ...suggestion,
        normalizedHref: normalizedSearchText(suggestion.href),
        normalizedLabel: normalizedSearchText(suggestion.label),
    }));
    const match = candidates.find((candidate) => (
        candidate.normalizedLabel === normalizedQuery
    )) ?? candidates.find((candidate) => (
        candidate.normalizedLabel.startsWith(normalizedQuery)
    )) ?? candidates.find((candidate) => (
        candidate.normalizedLabel.includes(normalizedQuery)
        || candidate.normalizedHref.includes(normalizedQuery)
    ));

    return match?.href ?? null;
}
